import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Reacceptance banner + modal shown inside the Vendor Portal.
 *
 * Backend enforcement is already in place for new-work actions
 * (POST /rfqs/{id}/quote and /rfqs/{id}/bafo/submit). This component is the
 * visible cue — statements, profile, documents and support remain accessible
 * even while re-acceptance is pending.
 */
export default function VendorAgreementGate() {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [terms, setTerms] = useState(false);
  const [privacyAck, setPrivacyAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const response = await api.get("/vendor/legal/state");
      setState(response.data);
    } catch (error) {
      // Non-vendor callers get 403; silently ignore.
    }
  };
  useEffect(() => { load(); }, []);

  const openModal = async () => {
    try {
      const applicantType = state?.required?.applicable_to || "VENDOR";
      const response = await api.get(`/legal/current?applicant_type=${applicantType}`);
      setCurrent(response.data);
      setTerms(false);
      setPrivacyAck(false);
      setOpen(true);
    } catch (error) {
      toast.error("Could not load the current agreement");
    }
  };

  const submit = async () => {
    if (!(terms && privacyAck)) {
      toast.error("Please tick both acknowledgements");
      return;
    }
    setSubmitting(true);
    try {
      const idempotency_key = `vendor-reaccept-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      await api.post("/vendor/legal/accept", {
        agreement_id: current.agreement.agreement_id,
        schedule_id: current.schedule.schedule_id,
        accepted_terms: terms,
        accepted_privacy: privacyAck,
        idempotency_key,
      });
      toast.success("Agreement accepted — you can now submit new quotes");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not record acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  if (!state?.needs_reacceptance) return null;

  return (
    <>
      <Alert variant="destructive" data-testid="vendor-agreement-gate">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <div className="flex items-center justify-between gap-4">
            <div>
              <strong>Re-acceptance required.</strong> The platform has published a newer Vendor/Technician Service Agreement or Commercial Schedule.
              You can continue to view statements, documents and support — but you cannot submit new quotes until you accept the latest version.
            </div>
            <Button data-testid="vendor-agreement-open" onClick={openModal}>Review &amp; accept</Button>
          </div>
        </AlertDescription>
      </Alert>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="vendor-agreement-modal">
          <DialogHeader>
            <DialogTitle>
              <FileText className="inline h-5 w-5 mr-2" />
              Vendor / Technician Service Agreement
              {current?.agreement?.watermark && (
                <Badge className="ml-2" variant="secondary">{current.agreement.watermark}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {current && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">
                Agreement v{current.agreement.version} ({current.agreement.status}) · Schedule v{current.schedule.version} ({current.schedule.status})
              </div>
              <div className="max-h-80 overflow-y-auto border p-3 text-sm whitespace-pre-wrap font-mono bg-slate-50" data-testid="vendor-agreement-text">
                {current.agreement.text_md}
              </div>
              <div>
                <h4 className="font-semibold text-sm">Commercial Schedule</h4>
                <table className="w-full text-xs border mt-2" data-testid="vendor-agreement-schedule">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Category</th>
                      <th className="p-2 text-left">Commission</th>
                      <th className="p-2 text-left">Visiting fee</th>
                      <th className="p-2 text-left">Cancellation</th>
                      <th className="p-2 text-left">Warranty</th>
                      <th className="p-2 text-left">Weekly payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.schedule.rows.map((row, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{row.category_name}</td>
                        <td className="p-2">{row.commission_percent}%</td>
                        <td className="p-2">{row.visiting_fee}</td>
                        <td className="p-2">{row.cancellation_rules}</td>
                        <td className="p-2">{row.warranty_revisit}</td>
                        <td className="p-2">{row.weekly_payout_rule}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={terms} onCheckedChange={setTerms} data-testid="vendor-agreement-ack-terms" />
                  <span>I have read and accept the Vendor/Technician Service Agreement and the displayed Commercial Schedule, including commission, visiting-fee, cancellation and payout rules.</span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={privacyAck} onCheckedChange={setPrivacyAck} data-testid="vendor-agreement-ack-privacy" />
                  <span>I acknowledge the Privacy Notice and authorized use of my information for verification, assignments and payouts.</span>
                </label>
              </div>
              <div className="flex justify-end">
                <Button data-testid="vendor-agreement-submit" onClick={submit} disabled={submitting || !(terms && privacyAck)}>
                  Accept and continue
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
